import { Component, ElementRef, EventEmitter, Output, Renderer2 } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { saveAs } from 'file-saver';
import { lastValueFrom } from 'rxjs';

import { environment } from '@environment';
import { FileService } from '@services/file.service';
import { FormService } from '@services/form.service';
import { GlobalService } from '@services/global.service';
import { PageService } from '@services/page.service';
import { SharedService } from '@services/shared.service';
import { UserService } from '@services/user.service';
import { DataQueryOutputColumn, DataQueryOutputLinkTarget, DataQueryResult } from '@models/data-query-result';
import { ModalDataQueryConfigs } from '@models/modal-data-query-configs';
import { LinkBaseComponent } from '../link-base/link-base.component';
import { IValidatedUser } from '@models/validated-user';
import { SharedHeader } from '@models/shared-header';
import { PageDetail } from '@models/page-detail';
import { PageSummary } from '@models/page-summary';
import { DataQueryDetail } from '@models/data-query-detail';
import { DataQueryResultRow } from '@models/data-query-result-row';

@Component({
    selector: 'cp-data-query-results-base',
    templateUrl: './data-query-results-base.component.html',
    styleUrls: ['./data-query-results-base.component.less'],
    standalone: false
})
export class DataQueryResultsBaseComponent {
    @Output() refreshPage: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() showModalResults: EventEmitter<ModalDataQueryConfigs> = new EventEmitter<ModalDataQueryConfigs>();

    protected _pendingDownloads: DataQueryOutputColumn[] = [];

    protected _currencyFormat: Intl.NumberFormat;
    protected _dateFormat: Intl.DateTimeFormat;
    protected _timeFormat: Intl.DateTimeFormat;
    protected _dateTimeFormat: Intl.DateTimeFormat;

    showPartsBook: boolean = false;
    partsBookUrl: SafeResourceUrl;

    constructor(
        protected _globalService: GlobalService,
        protected _formService: FormService,
        protected _elementRef: ElementRef,
        protected _renderer: Renderer2,
        protected _router: Router,
        protected _pageService: PageService,
        protected _sharedService: SharedService,
        protected _userService: UserService,
        protected _fileService: FileService,
        protected _sanitizer: DomSanitizer
    ) {
        this._currencyFormat = new Intl.NumberFormat(this._globalService.getLanguage(), {
            maximumFractionDigits: 2,
            minimumFractionDigits: 2
        });

        this._dateFormat = new Intl.DateTimeFormat(this._globalService.getLanguage());
        // this.dateFormat = new Intl.DateTimeFormat(this._globalService.getLanguage(), {
        //     month: "2-digit",
        //     day: "2-digit",
        //     year: "numeric"
        // });

        this._timeFormat = new Intl.DateTimeFormat(this._globalService.getLanguage(), {
            hour12: true,
            hour: "2-digit",
            minute: "2-digit"
        });

        this._dateTimeFormat = new Intl.DateTimeFormat(this._globalService.getLanguage(), {
            hour12: true,
        });
        // this.dateTimeFormat = new Intl.DateTimeFormat(this._globalService.getLanguage(), {
        //     hour12: true,
        //     month: "2-digit",
        //     day: "2-digit",
        //     year: "numeric",
        //     hour: "2-digit",
        //     minute: "2-digit"
        // });
    }

    executeDataQuery(dq: string, idx: number): void {}

    executeLink(column: DataQueryOutputColumn, fieldIndex: number | null = null, control: LinkBaseComponent = null): void {
        if (column.isModal) {
            if (control) {
                control.busy = true;
            }
            switch (column.linkTarget) {
                case 'anchor':
                case 'image':
                    this.showModal(column.destinationRoute, column.destinationRouteParams, control);
                    break;
                case 'anchorUrl':
                case 'imageUrl':
                    this._router.navigateByUrl(column.destinationRoute);
                    break;
                case 'filestream/string':
                case 'filestream/image':
                    this.getFilestream(column.destinationRoute, column.destinationRouteParams, column.specialType, control);
                    break;
                case 'dataquery/image':
                case 'dataquery/string':
                    this.showModal(column.destinationRoute, column.destinationRouteParams, control);
                    break;
            }
        } else {
            switch (column.linkTarget) {
                case 'anchor':
                case 'image':
                    this._router.navigate([column.destinationRoute], {
                        queryParams: column.destinationRouteParams,
                    });
                    break;
                case 'anchorUrl':
                case 'imageUrl':
                    this._router.navigateByUrl(column.destinationRoute);
                    break;
                case 'filestream/string':
                case 'filestream/image':
                    this.getFilestream(column.destinationRoute, column.destinationRouteParams, column.specialType, control);
                    this._pendingDownloads.push(column);
                    break;
                case 'filestream/iframe':
                    column.specialType = 'partsbook';
                    this.getFilestreamAsUrl(column.destinationRoute, column.destinationRouteParams, column.specialType, control);
                    break;
                case 'dataquery/image':
                case 'dataquery/string':
                    this.executeDataQuery(column.destinationRouteParams, fieldIndex);
                    break;
            }
        }
    }

    async showModal(destination: string, params: object, callingControl: LinkBaseComponent = null): Promise<void> {
        const pageId: string = this.getPageId(destination);
        const formData = this.constructFormData(pageId, params);
        const pageHeader: SharedHeader =
            await lastValueFrom(this._sharedService.getHeader(pageId)).then((header: SharedHeader) => header);
        const pageDetails: PageDetail[] =
            await lastValueFrom(this._pageService.getPage(pageId)).then((summary: PageSummary) => summary.Details);
        const dataQueryId: string = pageDetails[0].DataQueryId;
        const formId: string = pageDetails[0].DetailId;

        const results =
            await lastValueFrom(this._formService.submitDataQuery(formId, dataQueryId, formData)).then((r: DataQueryResult[]) => r);
        if (callingControl) {
            callingControl.busy = false;
        }

        this.showModalResults.emit({
            title: pageHeader.Title,
            dataQueryResults: results,
        });
    }

    getFilestream(fileName: string, streamType: string, fileType: string, control: LinkBaseComponent): void {
        let type: string  = environment.System === 'USNR' ? 'pubweb' : 'customerportal';

        const pdfFile: boolean = fileName.toLowerCase().endsWith('.pdf') || fileType.toLowerCase() == "crm";
        this._fileService.getFilestream(type, fileName, fileType, streamType, pdfFile).subscribe({
            next: (response: any) => {
                if (!response) {
                    alert('File does not exist.');
                    return;
                }

                if (pdfFile) {
                    const blobContents: Blob = FileService.b64toBlob(response, 'application/octet-stream', 512);
                    saveAs(blobContents, fileType.toLowerCase() === "crm" ? "ServiceCaseReport" + Date.now() + ".pdf" : fileName);
                } else {
                    saveAs(response, fileName);
                }
            },
            error: (error: any) => {
                alert('Error fetching file.');
            },
            complete: () => {
                if (control) {
                    control.busy = false;
                }
            }
        });
    }

    getFilestreamAsUrl(fileName: string, streamType: string = "", fileType: string = "partsbook", control: LinkBaseComponent): void {
        // type supported is only "partsbook" at this time
        this._fileService.getFilestream("partsbook", fileName, fileType, streamType, true).subscribe({
            next: (response: any) => {
                if (!response) {
                    alert('File does not exist.');
                    return;
                }

                const blobContents: Blob = FileService.b64toBlob(response, 'text/html', 512);
                const drawingUrl: string = URL.createObjectURL(blobContents);
                this.showPartsBook = true;
                this.partsBookUrl = this._sanitizer.bypassSecurityTrustResourceUrl(drawingUrl);
                // this.showPartsBookModal(this.partsBookUrl)
                // window.open(drawingUrl, '_blank');
                // this._router.navigate(['partsBook'], { queryParams: { partsbook: drawingUrl }});
            },
            error: (error: any) => {
                alert('Error fetching file.');
            },
            complete: () => {
                if (control) {
                    control.busy = false;
                }
            }
        });
    }

    private getPageId(url: string): string {
        const startIdx: number = url.indexOf('/page/');
        if (startIdx > 0) {
            const endIdx: number = url.indexOf('?');
            if (endIdx > 0) {
                return url.substring(startIdx + 6, endIdx - startIdx);
            } else {
                return url.substring(startIdx + 6);
            }
        }
        return '';
    }

    protected constructFormData(pageId: string, params: object): any {
        const formData: { [id: string]: string } = {};
        formData['objectId'] = pageId;
        formData['objectType'] = 'page';
        formData['LanguageId'] = this._globalService.getLanguage();

        Object.keys(params).forEach((key: string) => {
            formData['QueryString-' + key.toLowerCase()] = params[key];
        });

        const user: IValidatedUser = this._userService.getCurrentUser();
        if (user) {
            formData['loginContext'] = user.UserId;
        }

        return formData;
    }

    protected parseDestination(fieldValues: any[], columnDefinitions: DataQueryDetail[], sourceIndex: number): DataQueryOutputLinkTarget {
        const refSortOrder: number = columnDefinitions[sourceIndex].ReferenceSortOrder;
        if (refSortOrder === 0) {
            return null;
        } else {
            for (let i = 0; i < columnDefinitions.length; ++i) {
                if (columnDefinitions[i].SortOrder === refSortOrder) {
                    if (columnDefinitions[i].DetailType === 'TargetRelativeURL') {
                        const target: DataQueryOutputLinkTarget = this.parseUrl(fieldValues[i]);
                        // target.params['dt'] = 1;
                        target.isModal = columnDefinitions[i].IsModal;
                        return target;
                    } else if (columnDefinitions[i].DetailType === 'TargetURL') {
                        const target: DataQueryOutputLinkTarget = this.parseUrl(fieldValues[i]);
                        target.isModal = columnDefinitions[i].IsModal;
                        return target;
                    } else if (columnDefinitions[i].DetailType === 'TargetSQLAction') {
                        return {
                            route: 'dataquery/',
                            params: fieldValues[i],
                            streamType: '',
                            isModal: columnDefinitions[i].IsModal,
                        };
                    } else {
                        if (fieldValues[i] === '') {
                            return {
                                route: '',
                                params: '',
                                streamType: '',
                                isModal: false,
                            };
                        }

                        return {
                            route: 'filestream/' + columnDefinitions[i].FilestreamType,
                            params: fieldValues[i],
                            streamType: columnDefinitions[i].FilestreamTypeId,
                            isModal: columnDefinitions[i].IsModal,
                        };
                    }
                }
            }
            return null;
        }
    }

    private parseUrl(url: string): DataQueryOutputLinkTarget {
        let idx: number;

        const linkTarget: DataQueryOutputLinkTarget = new DataQueryOutputLinkTarget();

        if (!url || url === '') {
            linkTarget.route = '';
            return linkTarget;
        }

        if (url.startsWith("http")) {
            linkTarget.route = url;
            linkTarget.params = {};
            return linkTarget;
        }

        idx = url.indexOf("?");
        if (idx > 0) {
            linkTarget.route = url.substring(0, idx);
            url = url.substring(idx + 1);
        } else {
            linkTarget.route = url;
            linkTarget.params = {};
            return linkTarget;
        }

        linkTarget.params = {};
        const params: string[] = url.split("&");
        params.forEach((param: string) => {
            idx = param.indexOf("=");
            linkTarget.params[param.substring(0, idx)] = param.substring(idx + 1);
        });

        return linkTarget;
    }

    protected formatColumn(resultSet: DataQueryResult, fieldIdx: number, data: DataQueryResultRow): DataQueryOutputColumn | null {
        const currentColumn: DataQueryOutputColumn = new DataQueryOutputColumn();
        currentColumn.imageWidth = -1;
        currentColumn.imageHeight = -1;
        currentColumn.alignment = resultSet.Details[fieldIdx].Alignment;
        currentColumn.width = resultSet.Details[fieldIdx].Width;

        switch (resultSet.Details[fieldIdx].DetailType) {
            case "IOString":
                currentColumn.value = data.FieldValues[fieldIdx];
                currentColumn.alignment = resultSet.Details[fieldIdx].Alignment;
                currentColumn.name = resultSet.Details[fieldIdx].ColumnName;
                currentColumn.specialType = "iostring";
                break;
            case "IOStringRequired":
                currentColumn.value = data.FieldValues[fieldIdx];
                currentColumn.name = resultSet.Details[fieldIdx].ColumnName;
                currentColumn.specialType = "iostringrequired";
                break;
            case "FieldMoney":
                currentColumn.value = this._currencyFormat.format(data.FieldValues[fieldIdx]);
                break;
            case "FieldString":
                if (resultSet.Details[fieldIdx].ReferenceSortOrder > 0) {
                    const target: DataQueryOutputLinkTarget = this.parseDestination(data.FieldValues, resultSet.Details, fieldIdx);
                    if (target.route === "") {
                        currentColumn.linkTarget = "";
                    } else {
                        if (target.route.substring(0, 4) === "http") {
                            currentColumn.destinationRoute = target.route;
                            currentColumn.linkTarget = "anchorUrl";
                        } else if (target.route.substring(0, 11) === "filestream/") {
                            currentColumn.destinationRoute = target.params;
                            currentColumn.destinationRouteParams = target.route.substring(target.route.indexOf("/") + 1);
                            currentColumn.specialType = target.streamType;
                            currentColumn.linkTarget = "filestream/string";
                        } else if (target.route.substring(0, 10) === "dataquery/") {
                            currentColumn.destinationRoute = "/";
                            currentColumn.destinationRouteParams = target.params;
                            currentColumn.linkTarget = "dataquery/string";
                        } else {
                            currentColumn.destinationRoute = `/${target.route.toLowerCase()}`;
                            currentColumn.destinationRouteParams = target.params;
                            currentColumn.linkTarget = "anchor";
                        }
                    }
                }
                currentColumn.value = data.FieldValues[fieldIdx];
                break;
            case "FieldDate":
                if (data.FieldValues[fieldIdx] === null || data.FieldValues[fieldIdx] === '') {
                    currentColumn.value = '';
                } else {
                    const d: Date = new Date(data.FieldValues[fieldIdx]);
                    if (d.getFullYear() > 1900) {
                        currentColumn.value = this._dateFormat.format(d);
                    } else {
                        currentColumn.value = "";
                    }
                }
                break;
            case "FieldTime":
                const t: Date = new Date(data.FieldValues[fieldIdx]);
                currentColumn.value = this._timeFormat.format(t);
                break;
            case "FieldDateTime":
                if (data.FieldValues[fieldIdx] === null || data.FieldValues[fieldIdx] === '') {
                    currentColumn.value = '';
                } else {
                    const dt: Date = new Date(data.FieldValues[fieldIdx]);
                    if (dt.getFullYear() > 1900) {
                        currentColumn.value = this._dateTimeFormat.format(dt);
                    } else {
                        currentColumn.value = "";
                    }
                }
                break;
            case "FieldNumber":
                currentColumn.value = data.FieldValues[fieldIdx];
                break;
            case "TargetFilestream":
            case "TargetRelativeURL":
            case "TargetURL":
            case "TargetSQLAction":
            case 'TargetFilestreamUrl':
                return null;
            case "FieldImage":
                if (resultSet.Details[fieldIdx].ReferenceSortOrder > 0) {
                    if (data.FieldValues[0] == "CAS-89378-G0Z0") {
                        data.FieldValues[fieldIdx] = data.FieldValues[fieldIdx];
                    }
                    const target: DataQueryOutputLinkTarget = this.parseDestination(data.FieldValues, resultSet.Details, fieldIdx);
                    if (target.route === "") {
                        data.FieldValues[fieldIdx] = "";
                        currentColumn.linkTarget = "";
                    } else {
                        if (target.route.substring(0, 4) === "http") {
                            currentColumn.destinationRoute = target.route;
                            currentColumn.linkTarget = "imageUrl";
                        } else if (target.route.substring(0, 11) === "filestream/") {
                            currentColumn.destinationRoute = target.params;
                            currentColumn.destinationRouteParams = target.route.substring(target.route.indexOf("/") + 1);
                            currentColumn.specialType = target.streamType;
                            currentColumn.linkTarget = "filestream/image";
                        } else if (target.route.substring(0, 10) === "dataquery/") {
                            currentColumn.destinationRoute = "/";
                            currentColumn.destinationRouteParams = target.params;
                            currentColumn.linkTarget = "dataquery/image";
                        } else {
                            currentColumn.destinationRoute = `/${target.route.toLowerCase()}`;
                            currentColumn.destinationRouteParams = target.params;
                            currentColumn.linkTarget = "image";
                        }
                    }
                }
                currentColumn.imageWidth = resultSet.Details[fieldIdx].Width;
                currentColumn.value = data.FieldValues[fieldIdx];
                break;
            case 'FieldIframe':
                if (resultSet.Details[fieldIdx].ReferenceSortOrder > 0) {
                    const target: DataQueryOutputLinkTarget = this.parseDestination(data.FieldValues, resultSet.Details, fieldIdx);
                    currentColumn.isModal = target.isModal;
                    currentColumn.destinationRoute = target.params;
                    currentColumn.destinationRouteParams = target.route.substring(target.route.indexOf('/') + 1);
                    currentColumn.specialType = target.streamType;
                    currentColumn.linkTarget = 'filestream/iframe';
                }
                currentColumn.value = data.FieldValues[fieldIdx];
                currentColumn.imageWidth = resultSet.Details[fieldIdx].Width;
                break;
            case "FieldFilestream":
                currentColumn.specialType = resultSet.Details[fieldIdx].FilestreamType.toLowerCase();
                currentColumn.value = data.FieldValues[fieldIdx];
                currentColumn.imageWidth = resultSet.Details[fieldIdx].ImageWidth;
                currentColumn.imageHeight = resultSet.Details[fieldIdx].ImageHeight;
                break;
            default:
                currentColumn.value = data.FieldValues[fieldIdx];
        }

        return currentColumn;
    }
}
